import {
  type TWebAnalyticsOverviewMetric,
  type TWebAnalyticsOverviewPoint,
} from '@mahawi/eshop-common';
import { Card, Col, Row } from 'antd';
import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { type ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { COLOR, FONT_SIZE, SPACING } from '../../theme/tokens';
import { formatMetric } from './format';

export interface ITrendMetric {
  metric: TWebAnalyticsOverviewMetric;
  title: string;
  isReversed?: boolean;
}

export interface ITrendChartPoint {
  date: string;
  current: number | null;
  compare: number | null;
  compareDate: string | null;
}

export const TREND_METRICS: ITrendMetric[] = [
  { metric: 'clicks', title: 'Search clicks' },
  { metric: 'impressions', title: 'Search impressions' },
  { metric: 'ctr', title: 'CTR' },
  { metric: 'position', title: 'Average position', isReversed: true },
  { metric: 'cfPageViews', title: 'Page views (Cloudflare)' },
  { metric: 'revenueEur', title: 'Revenue (own orders)' },
  { metric: 'orders', title: 'Orders (own orders)' },
];

export const toChartData = (
  series: TWebAnalyticsOverviewPoint[],
  compareSeries: TWebAnalyticsOverviewPoint[] | null,
  metric: TWebAnalyticsOverviewMetric,
): ITrendChartPoint[] =>
  series.map(
    (point: TWebAnalyticsOverviewPoint, index: number): ITrendChartPoint => {
      const comparePoint = compareSeries?.[index];

      return {
        date: point.date,
        current: point[metric],
        compare: comparePoint ? comparePoint[metric] : null,
        compareDate: comparePoint ? comparePoint.date : null,
      };
    },
  );

export const formatTooltipValue =
  (metric: TWebAnalyticsOverviewMetric) =>
  (value: ValueType | null | undefined): string =>
    formatMetric(metric, typeof value === 'number' ? value : null);

export function MetricTrend({
  trend,
  series,
  compareSeries,
}: {
  trend: ITrendMetric;
  series: TWebAnalyticsOverviewPoint[];
  compareSeries: TWebAnalyticsOverviewPoint[] | null;
}): React.ReactElement {
  return (
    <Card size="small" title={trend.title}>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart
          data={toChartData(series, compareSeries, trend.metric)}
          margin={{ top: SPACING.sm, right: SPACING.sm, bottom: 0, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={COLOR.borderDefault} />
          <XAxis dataKey="date" tick={{ fontSize: FONT_SIZE.xs }} />
          <YAxis
            tick={{ fontSize: FONT_SIZE.xs }}
            width={50}
            reversed={trend.isReversed}
            domain={['auto', 'auto']}
          />
          <RechartsTooltip formatter={formatTooltipValue(trend.metric)} />
          <Line
            type="monotone"
            dataKey="current"
            name="Current"
            stroke={COLOR.primary}
            strokeWidth={2}
            dot={false}
            connectNulls={false}
          />
          {compareSeries ? (
            <Line
              type="monotone"
              dataKey="compare"
              name="Compare"
              stroke={COLOR.gold[5]}
              strokeDasharray="4 2"
              dot={false}
              connectNulls={false}
            />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

function TrendChart({
  series,
  compareSeries,
}: {
  series: TWebAnalyticsOverviewPoint[];
  compareSeries: TWebAnalyticsOverviewPoint[] | null;
}): React.ReactElement {
  return (
    <Row gutter={[SPACING.lg, SPACING.lg]}>
      {TREND_METRICS.map((trend: ITrendMetric): React.ReactElement => (
        <Col key={trend.metric} xs={24} md={12} xl={8}>
          <MetricTrend
            trend={trend}
            series={series}
            compareSeries={compareSeries}
          />
        </Col>
      ))}
    </Row>
  );
}

export default TrendChart;
